import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { MapPin, Plus, X, Save } from 'lucide-react';

export default function VendorZones() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newZip, setNewZip] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try { const res = await api.get('/vendor/delivery-zones'); setZones(res.data.zones || []); } catch { toast.error('Failed'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const addZip = () => {
    if (!newZip.trim()) return;
    if (zones.includes(newZip.trim())) { toast.error('Already added'); return; }
    setZones([...zones, newZip.trim()]);
    setNewZip('');
  };

  const removeZip = (zip) => setZones(zones.filter(z => z !== zip));

  const saveZones = async () => {
    setSaving(true);
    try {
      await api.put('/vendor/delivery-zones', { zones });
      toast.success('Delivery zones updated');
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Delivery Zones</h1>
        <p className="text-muted-foreground mt-1">Manage zip codes for your delivery area.</p>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-medium flex items-center gap-2"><MapPin className="h-4 w-4" /> Zip Codes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Enter zip code (e.g., 28202)"
              value={newZip}
              onChange={e => setNewZip(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addZip()}
              data-testid="zone-zip-input"
            />
            <Button variant="outline" onClick={addZip} data-testid="zone-add-button">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {zones.length > 0 ? zones.map(zip => (
              <Badge key={zip} variant="secondary" className="text-sm py-1.5 px-3 gap-1.5">
                <MapPin className="h-3 w-3" />
                {zip}
                <button onClick={() => removeZip(zip)} className="ml-1 hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )) : <p className="text-muted-foreground text-sm">No delivery zones configured</p>}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={saveZones} disabled={saving} data-testid="zones-save-button">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Zones'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
